import React, { useState, useEffect } from 'react';
import { api } from '../../App';
import { Receipt, ArrowRight } from 'lucide-react';

/**
 * Tax info banner.
 *
 * Renders on the Provider Dashboard above the tabs when the provider
 * hasn&apos;t completed their HMRC tax-info card. Tap-through scrolls
 * them to the Security tab. Hides itself once complete.
 */
export default function TaxInfoBanner({ onTapThrough }) {
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    let cancelled = false;
    const fetchStatus = () => {
      api.get('/tax/profile/me')
        .then(res => { if (!cancelled) setStatus(res.data?.status || 'missing'); })
        .catch(() => { if (!cancelled) setStatus('error'); });
    };
    fetchStatus();
    // Listen for in-page tax-profile updates from TaxInfoCard so the
    // banner refreshes without a full reload.
    const onUpdate = () => fetchStatus();
    window.addEventListener('zuriya:tax-profile-updated', onUpdate);
    return () => {
      cancelled = true;
      window.removeEventListener('zuriya:tax-profile-updated', onUpdate);
    };
  }, []);

  if (status === 'loading' || status === 'complete' || status === 'error') return null;

  const isMissing = status === 'missing';
  return (
    <button
      type="button"
      onClick={onTapThrough}
      className="w-full mb-4 px-4 py-3 rounded-lg border border-amber-200 bg-amber-50 flex items-center gap-3 text-left hover:bg-amber-100/70 transition-colors"
      data-testid="tax-info-banner"
    >
      <Receipt className="h-5 w-5 text-amber-600 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-amber-900">
          {isMissing
            ? 'Tax information needed for HMRC reporting'
            : 'Tax information needs reviewing'}
        </p>
        <p className="text-xs text-amber-700/80 mt-0.5">
          UK marketplaces must report provider earnings to HMRC each January.
          Add your tax identifier so we can include you in this year&rsquo;s return.
        </p>
      </div>
      <ArrowRight className="h-4 w-4 text-amber-600 shrink-0" />
    </button>
  );
}
